import { DataTypeLocator } from '@helo/cms'

import { sanitizeFilterForCube } from '../../components/FilterEditor/stringFilterInputAdapters'

/**
 * Maps a source filter field key to the acceptable target field keys, for
 * datasources that expose the same semantic field under different names
 * (e.g. a cube MoM view using `confirmed_on_board` instead of
 * `confirmed_on_board_date`). Configured per view in static content —
 * see the FilterEditor widget props in the view config.
 */
export type FilterFieldAliases = Record<string, string[]>

/** Structural slice of a query node needed to discover its filter field keys. */
export type FilterFieldSource = {
  queryDefinition?: {
    variableMappers?: {
      locator?: DataTypeLocator
      mapper?: { fields?: { key: string }[] }
    }[]
  }
}

/** Reads the filter field keys a target query accepts from its variable mapper. */
export const getTargetFilterFieldKeys = (
  targetQueryNode: FilterFieldSource | undefined,
  variableName: string,
): string[] => {
  const mapper = targetQueryNode?.queryDefinition?.variableMappers?.find(m =>
    m.locator?.some(entry => entry[0] === 'key' && entry[1] === variableName),
  )
  return (mapper?.mapper?.fields ?? []).map(field => field.key)
}

/**
 * Maps a source filter object onto a target query's field keys, translating
 * aliased field names and dropping fields the target does not accept.
 */
export const mapFilterToTargetFields = (
  sourceFilter: Record<string, unknown>,
  targetKeys: string[],
  fieldAliases: FilterFieldAliases = {},
): Record<string, unknown> => {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(sourceFilter)) {
    if (value == null) continue
    const candidates = fieldAliases[key] ?? [key]
    const targetKey = candidates.find(candidate =>
      targetKeys.includes(candidate),
    )
    if (targetKey) {
      result[targetKey] = value
    }
  }
  return result
}

export type CubeDashboardFilterTarget = {
  queryId: string
  /** The query variable this filter is written to (typically `filter`). */
  variableName: string
  targetKeys: string[]
}

/**
 * Sanitizes a dashboard filter for Cube GraphQL (strips Prisma-only `mode`,
 * normalizes list operators), then maps it onto each target variable's accepted
 * field keys. Used by FilterEditorWidget before primary + fan-out updates.
 */
export const prepareCubeDashboardFilterUpdate = (
  rawFilter: Record<string, unknown>,
  targets: CubeDashboardFilterTarget[],
  fieldAliases: FilterFieldAliases = {},
): {
  sanitizedFilter: Record<string, unknown>
  targetFilters: Array<{
    queryId: string
    variableName: string
    filter: Record<string, unknown>
  }>
} => {
  const sanitizedFilter = sanitizeFilterForCube(rawFilter)
  return {
    sanitizedFilter,
    targetFilters: targets.map(({ queryId, variableName, targetKeys }) => ({
      queryId,
      variableName,
      filter: mapFilterToTargetFields(
        sanitizedFilter,
        targetKeys,
        fieldAliases,
      ),
    })),
  }
}
